import type { IDSFormData, IDSValidationResult } from '@/types/ids';

/**
 * Validate application number format
 */
export function validateApplicationNumber(appNo: string): IDSValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!appNo || !appNo.trim()) {
    errors.push('Application number is required');
    return { isValid: false, errors, warnings };
  }

  // Basic format validation
  const cleaned = appNo.replace(/[^0-9]/g, '');
  if (cleaned.length < 7) {
    errors.push('Application number must be at least 7 digits');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate docket number format
 */
export function validateDocketNumber(docketNo: string): IDSValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!docketNo || !docketNo.trim()) {
    // Docket number is optional
    return { isValid: true, errors, warnings };
  }

  if (docketNo.length > 50) {
    warnings.push('Docket number is unusually long');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate references text
 */
export function validateReferencesText(text: string): IDSValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!text || !text.trim()) {
    errors.push('At least one reference is required');
    return { isValid: false, errors, warnings };
  }

  // Count lines
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length === 0) {
    errors.push('No valid references found');
  }

  if (lines.length > 100) {
    warnings.push(`Large number of references (${lines.length}). Consider splitting into multiple submissions.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate complete IDS form
 */
export function validateCompleteIDSForm(formData: IDSFormData): IDSValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate application info
  if (!formData.appNo && !formData.docketNo) {
    errors.push('Application number or docket number is required');
  }

  // Validate IDS name
  if (!formData.idsName || !formData.idsName.trim()) {
    errors.push('IDS name is required');
  }

  // Validate references - at least one type is required
  const hasUsRefs = formData.referencesText && formData.referencesText.trim();
  const hasForeignRefs = formData.foreignReferencesText && formData.foreignReferencesText.trim();
  const hasNplRefs = formData.nplText && formData.nplText.trim();

  if (!hasUsRefs && !hasForeignRefs && !hasNplRefs) {
    errors.push('At least one reference is required (US patent/publication, foreign patent, or NPL)');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
